import { useState, type ReactNode } from "react";
import logoUrl from "../assets/logo.png";

export function Wordmark({ size = 22 }: { size?: number }) {
  return (
    <div className="brand">
      <BrainMark size={size + 8} />
      <div>
        <div className="wordmark" style={{ fontSize: size }}>
          based<span className="ai">AI</span>
        </div>
        <div className="tag">IT&apos;S OPEN</div>
      </div>
    </div>
  );
}

/** The basedAI mark — the electric Pepe app icon. `object-fit: cover` crops the source letterbox,
 *  and the blue glow ties it to the theme accent. */
export function BrainMark({ size = 28 }: { size?: number }) {
  return (
    <img
      className="logo-mark"
      src={logoUrl}
      width={size}
      height={size}
      alt="basedAI"
      style={{ width: size, height: size }}
    />
  );
}

export function Card({
  title,
  sub,
  right,
  children,
}: {
  title?: string;
  sub?: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      {(title || right) && (
        <div className="card-head">
          <div>
            {title && <div className="card-title">{title}</div>}
            {sub && <div className="card-sub">{sub}</div>}
          </div>
          <div className="spacer" />
          {right}
        </div>
      )}
      <div className="card-body">{children}</div>
    </div>
  );
}

export function Stat({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function Badge({ kind = "default", children }: { kind?: "default" | "ok" | "warn" | "mystic"; children: ReactNode }) {
  return (
    <span className={`badge ${kind === "default" ? "" : kind}`}>
      <span className="dot" />
      {children}
    </span>
  );
}

export function Notice({ kind = "default", children }: { kind?: "default" | "warn" | "error"; children: ReactNode }) {
  return <div className={`notice ${kind === "default" ? "" : kind}`}>{children}</div>;
}

/**
 * A button that runs an async action (usually a tx), showing a spinner while pending and surfacing
 * any error inline. Keeps every write path consistent without a form library.
 */
export function ActionButton({
  onRun,
  children,
  disabled,
  variant = "primary",
  onDone,
}: {
  onRun: () => Promise<string | void>;
  children: ReactNode;
  disabled?: boolean;
  variant?: "primary" | "ghost";
  onDone?: (result: string | void) => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();

  async function run() {
    setError(undefined);
    setPending(true);
    try {
      const result = await onRun();
      onDone?.(result);
    } catch (e: unknown) {
      setError(cleanError(e));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <button className={`btn ${variant === "ghost" ? "ghost" : ""}`} onClick={run} disabled={disabled || pending}>
        {pending ? <span className="spin" /> : children}
      </button>
      {error && <Notice kind="error">{error}</Notice>}
    </div>
  );
}

export function cleanError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  // viem/wallet errors are verbose; keep the first meaningful line.
  const first = msg.split("\n")[0];
  if (/user rejected|denied/i.test(msg)) return "Transaction rejected in wallet.";
  return first.length > 200 ? first.slice(0, 200) + "…" : first;
}
